import { UserType } from "@istari/istari-client";
import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";

import { useAgentDisplayNames } from "~/agent/feature/use-agent-display-names";
import { userQueryOptions } from "~/user/data-source/user-query-options";
import { LEGACY_AGENT_DISPLAY_NAME, resolveUserDisplayName } from "~/user/utils/format-user-name";
import { getAgentFallbackId } from "~/user/utils/get-agent-fallback-id";
import { isLegacyZitadelAgent } from "~/user/utils/legacy-zitadel-agent";

export type UserDisplayName =
  | { status: "loading" }
  | { status: "error"; error: Error }
  | { status: "resolved"; kind: "human" | "agent"; displayName: string | null };

/**
 * Resolves registry user IDs, not principal IDs. Nameless agent users resolve through
 * their agent's display name; pending or failed agent lookups remain pending or failed.
 * Nameless legacy Zitadel agents have no agent record to look up and resolve to a fixed label.
 */
export function useUserDisplayNames(userIds: (string | null | undefined)[]): Record<string, UserDisplayName> {
  const idsKey = [...new Set(userIds.filter((id): id is string => !!id))].join("\0");
  const ids = useMemo(() => (idsKey ? idsKey.split("\0") : []), [idsKey]);
  const users = useQueries({
    queries: ids.map((id) => userQueryOptions.byId(id)),
    combine: (results) =>
      results.map((result) => ({ data: result.data, error: result.error, isPending: result.isPending })),
  });
  const agents = useAgentDisplayNames(
    users.map((result) => (!result.error && result.data ? getAgentFallbackId(result.data) : null)),
  );
  return useMemo(
    () =>
      Object.fromEntries(
        users.map((result, index): [string, UserDisplayName] => {
          const user = result.data;
          if (result.error) return [ids[index], { status: "error", error: result.error }];
          if (result.isPending || !user) return [ids[index], { status: "loading" }];
          const kind = user.userType === UserType.Agent ? "agent" : "human";
          const name = resolveUserDisplayName(user);
          if (name === null && isLegacyZitadelAgent(user)) {
            return [ids[index], { status: "resolved", kind, displayName: LEGACY_AGENT_DISPLAY_NAME }];
          }
          const agentId = name === null ? getAgentFallbackId(user) : null;
          if (!agentId) return [ids[index], { status: "resolved", kind, displayName: name }];
          const agent = agents[agentId];
          if (agent?.error) return [ids[index], { status: "error", error: agent.error }];
          if (!agent || agent.isLoading) return [ids[index], { status: "loading" }];
          return [ids[index], { status: "resolved", kind, displayName: agent.data ?? null }];
        }),
      ),
    [users, agents, ids],
  );
}
